import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { patternRecommendationService, RecommendationQuery, PatternRecommendationResponse } from '../services/patternRecommendationService';

interface PatternBasedRecommendationsProps {
  userId?: string;
  onSearchRecommendation: (query: string) => void;
  compact?: boolean;
  limit?: number;
}

const Container = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.compact ? 'var(--gap-sm)' : 'var(--gap-md)'};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--gap-sm);
`;

const SectionTitle = styled.h3<{ compact?: boolean }>`
  font-size: ${props => props.compact ? 'var(--font-size-sm)' : 'var(--font-size-md)'};
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--gap-xs);
`;

const PatternSummary = styled.p<{ compact?: boolean }>`
  font-size: ${props => props.compact ? '11px' : 'var(--font-size-xs)'};
  color: var(--color-text-secondary);
  margin: 0 0 var(--gap-sm) 0;
  line-height: 1.4;
  font-style: italic;
`;

const CategorySection = styled.div`
  margin-bottom: var(--gap-md);
`;

const CategoryTitle = styled.h4`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  margin: 0 0 var(--gap-xs) 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecommendationsList = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.compact ? '6px' : 'var(--gap-xs)'};
`;

const RecommendationCard = styled.button<{ compact?: boolean }>`
  background: var(--color-surface);
  border: 1px solid rgba(var(--color-border-rgb, 51, 65, 85), 0.3);
  border-radius: var(--radius-md);
  padding: ${props => props.compact ? '8px 10px' : '10px 12px'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(var(--color-primary-rgb, 99, 102, 241), 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const RecommendationText = styled.div<{ compact?: boolean }>`
  font-size: ${props => props.compact ? '12px' : 'var(--font-size-sm)'};
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  line-height: 1.3;
  margin-bottom: ${props => props.compact ? '2px' : '4px'};
`;

const RecommendationRationale = styled.div<{ compact?: boolean }>`
  font-size: ${props => props.compact ? '10px' : '11px'};
  color: var(--color-text-secondary);
  line-height: 1.3;
  opacity: 0.8;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gap-lg);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const ErrorState = styled.div`
  padding: var(--gap-md);
  background: rgba(var(--color-error-rgb, 220, 38, 127), 0.1);
  border: 1px solid rgba(var(--color-error-rgb, 220, 38, 127), 0.3);
  border-radius: var(--radius-md);
  color: var(--color-error, #dc2626);
  font-size: var(--font-size-sm);
  text-align: center;
`;

const EmptyState = styled.div`
  padding: var(--gap-lg);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.4;
`;


export const PatternBasedRecommendations: React.FC<PatternBasedRecommendationsProps> = ({
  userId,
  onSearchRecommendation,
  compact = false,
  limit
}) => {
  const [recommendations, setRecommendations] = useState<PatternRecommendationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      fetchRecommendations();
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRecommendations = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      
      const result = await patternRecommendationService.getPatternRecommendations(userId);
      setRecommendations(result);
    } catch (err) {
      console.error('Error fetching pattern recommendations:', err);
      setError('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };


  const handleRecommendationClick = (recommendation: RecommendationQuery) => {
    onSearchRecommendation(recommendation.text);
  };

  if (!userId) {
    return (
      <EmptyState>
        Sign in to get personalized recommendations based on your exploration patterns
      </EmptyState>
    );
  }

  if (loading && !recommendations) {
    return (
      <LoadingState>
        <div>Analyzing your interests...</div>
      </LoadingState>
    );
  }

  if (error && !recommendations) {
    return (
      <ErrorState>
        {error}
      </ErrorState>
    );
  }

  if (!recommendations) {
    return null;
  }

  // Group recommendations by category
  const groupedRecommendations = recommendations.recommendations.reduce((groups, rec) => {
    if (!groups[rec.category]) {
      groups[rec.category] = [];
    }
    groups[rec.category].push(rec);
    return groups;
  }, {} as Record<string, RecommendationQuery[]>);

  const categoryDisplayNames = patternRecommendationService.getCategoryDisplayNames();
  
  // Apply limit if specified
  const displayRecommendations = limit 
    ? recommendations.recommendations.slice(0, limit)
    : recommendations.recommendations;

  return (
    <Container compact={compact}>
      <SectionHeader>
        <SectionTitle compact={compact}>
          {recommendations.has_sufficient_data ? (
            <>
              Recommended for You
            </>
          ) : (
            <>
              Trending Explorations
            </>
          )}
        </SectionTitle>
      </SectionHeader>

      {limit ? (
        // Simple list view for limited display
        <RecommendationsList compact={compact}>
          {displayRecommendations.map((recommendation, index) => (
            <RecommendationCard
              key={index}
              compact={compact}
              onClick={() => handleRecommendationClick(recommendation)}
            >
              <RecommendationText compact={compact}>
                {recommendation.text}
              </RecommendationText>
              <RecommendationRationale compact={compact}>
                {recommendation.rationale}
              </RecommendationRationale>
            </RecommendationCard>
          ))}
        </RecommendationsList>
      ) : (
        // Grouped by category view for full display
        <div>
          {Object.entries(groupedRecommendations).map(([category, recs]) => (
            <CategorySection key={category}>
              <CategoryTitle>
                {categoryDisplayNames[category] || category}
              </CategoryTitle>
              <RecommendationsList compact={compact}>
                {recs.map((recommendation, index) => (
                  <RecommendationCard
                    key={index}
                    compact={compact}
                    onClick={() => handleRecommendationClick(recommendation)}
                  >
                    <RecommendationText compact={compact}>
                      {recommendation.text}
                    </RecommendationText>
                    <RecommendationRationale compact={compact}>
                      {recommendation.rationale}
                    </RecommendationRationale>
                  </RecommendationCard>
                ))}
              </RecommendationsList>
            </CategorySection>
          ))}
        </div>
      )}

      {recommendations.has_sufficient_data && recommendations.pattern_summary && !limit && (
        <PatternSummary compact={compact}>
          {recommendations.pattern_summary}
        </PatternSummary>
      )}

      {loading && recommendations && (
        <LoadingState>
          <div>Refreshing...</div>
        </LoadingState>
      )}
    </Container>
  );
};

export default PatternBasedRecommendations;